import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Label, HelperMessage } from '@atlaskit/form';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { token } from '@atlaskit/tokens';

import { Skeleton } from '@atlassian/jira-common-components-skeleton/src/styled.tsx';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { expVal, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { FieldContainer } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import type { StatusTransition } from '@atlassian/jira-issue-field-status/src/common/types.tsx';
import { StatusView } from '@atlassian/jira-issue-field-status/src/ui/status-view/index.tsx';
import { getNextgenSettingsWorkflowUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import { GIC_VIEW_SOURCE } from '@atlassian/jira-status-config-common/src/types.tsx';
import { useCombinedInlineConfigurationExperiment } from '@atlassian/jira-issue-field-status/src/ui/status-view/utils.tsx';
import { useIssueType } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { MANAGE_WORKFLOW_ACTION_SUBJECT_ID } from '../../../../common/constants/analytics.tsx';
import { useOpenManageFieldTab } from '../../../../controllers/use-open-manage-field-tab/useOpenManageFieldTab.tsx';
import { ManageFieldButton } from './manage-field-button/ManageFieldButton.tsx';
import messages from './messages.tsx';

export type Props = {
	isLoading: boolean;
	error?: Error;
	transitions: StatusTransition[];
	value: StatusTransition | null;
	onChange?: (
		transition: StatusTransition,
		analyticsEvent: UIAnalyticsEvent,
	) => void | Promise<void>;
	isMini: boolean;
	canConfigureStatuses?: boolean;
	issueTypeId: string | undefined;
	projectKey: string;
	projectId: string;
	projectType: ProjectType | null;
	onStatusCreatedOrUpdated?: () => Promise<void>;
};

const statusFieldMargin = gridSize * 2.25;

const StatusField = ({ onStatusCreatedOrUpdated, ...props }: Props) => {
	const { formatMessage } = useIntl();

	const [hasCreatedStatus, setHasCreatedStatus] = useState(false);
	const onStatusCreatedOrUpdatedCallback = useCallback(async () => {
		await onStatusCreatedOrUpdated?.();
		setHasCreatedStatus(true);
	}, [onStatusCreatedOrUpdated]);

	const defaultProps = {
		isLoading: false,
		footer: undefined,
		fieldId: 'gic-status-field',
	};

	const [inlineStatusConfigExp] = UNSAFE_noExposureExp('inline_status_add_and_rename_in_gic');
	const isInlineStatusConfigEnabled = inlineStatusConfigExp.get(
		'isInlineStatusConfigEnabled',
		false,
	);
	const combinedInlineConfigurationExperiment = useCombinedInlineConfigurationExperiment(
		props.canConfigureStatuses,
	);

	const isConfigureStatusAvailable =
		(isInlineStatusConfigEnabled || combinedInlineConfigurationExperiment.isEnabled) &&
		props.canConfigureStatuses;

	const isManageWorkflowAvailable =
		isConfigureStatusAvailable && props.projectType && props.issueTypeId;

	const getEnabledTransitions = (transitions: StatusTransition[]) =>
		transitions.filter((transition) => transition.isDisabled === false);

	const nextgenWorkflowUrl =
		(props.projectType &&
			props.issueTypeId &&
			getNextgenSettingsWorkflowUrl(props.projectType, props.projectKey, props.issueTypeId)) ||
		'';

	const openManageWorkflow = useOpenManageFieldTab({
		source: MANAGE_WORKFLOW_ACTION_SUBJECT_ID,
		url: nextgenWorkflowUrl,
	});

	const onOpenManageWorkflow = isConfigureStatusAvailable ? openManageWorkflow : noop;

	// used as footer with popup select component
	const manageWorkFlowButton = ManageFieldButton({
		url: nextgenWorkflowUrl,
		label: formatMessage(messages.manageWorkflowFooter),
		source: MANAGE_WORKFLOW_ACTION_SUBJECT_ID,
	});

	const getFieldContent = () => {
		if (props.isLoading) return <FieldSkeleton isMini={props.isMini} aria-busy />;

		if (!props.value?.to) {
			// error state
			return <FieldErrorSkeleton isMini={props.isMini} />;
		}

		if (props.error) return <FieldErrorSkeleton isMini={props.isMini} />;

		return (
			<StatusView
				{...defaultProps}
				appearance="button"
				value={props.value?.to}
				selectedTransition={props.value}
				transitions={getEnabledTransitions(props.transitions)}
				forceNoLabels
				isDefaultSelect={props.isMini}
				onChange={props.onChange}
				customNoOptionsMessage={formatMessage(messages.noEnabledTransitionsMessage)}
				shouldFitContainer={false}
				showConfigureStatusButtons={
					props.canConfigureStatuses &&
					expVal('inline_status_add_and_rename_in_gic', 'isInlineStatusConfigEnabled', false)
				}
				projectId={props.projectId}
				issueTypeId={props.issueTypeId}
				projectKey={props.projectKey}
				projectType={props.projectType}
				onStatusCreatedOrUpdated={onStatusCreatedOrUpdatedCallback}
				footer={isManageWorkflowAvailable ? manageWorkFlowButton : null}
				onOpenManageWorkflow={onOpenManageWorkflow}
				configureStatusSource={GIC_VIEW_SOURCE}
				autofocus={hasCreatedStatus}
				configureStatusButtonsEnabled={props.canConfigureStatuses}
			/>
		);
	};

	const [selectedIssueType] = useIssueType();
	const issueType = selectedIssueType?.label;
	const getMessage = () => {
		if (expVal('jira-itr-work-type-exp-1', 'isEnabled', false)) {
			return messages.warningMessageIssueTerminologyRefreshM4;
		}
		return expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.warningMessageIssueTermRefresh
			: messages.warningMessage;
	};

	const getHelpOrWarningText = () => {
		if (props.error) {
			return (
				<HelperMessage>
					<WarningIcon
						LEGACY_size="small"
						color={token('color.icon.warning')}
						label={formatMessage(messages.warningIconLabel)}
					/>
					{formatMessage(getMessage(), { issueType })}
				</HelperMessage>
			);
		}
		if (props.isMini) {
			return null;
		}

		return <HelperText>{formatMessage(messages.helperText)}</HelperText>;
	};

	return (
		<FieldContainer marginTop={statusFieldMargin}>
			<Label htmlFor={defaultProps.fieldId} id="gic-status-field-label">
				<LabelFlex>{formatMessage(messages.statusFieldLabel)}</LabelFlex>
			</Label>
			{getFieldContent()}
			{getHelpOrWarningText()}
		</FieldContainer>
	);
};

export default StatusField;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HelperText = styled.div({
	color: token('color.text.subtlest'),
	font: token('font.body.small'),
	marginTop: token('space.075'),
	marginBottom: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldSkeleton = styled(Skeleton)<{ isMini?: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isMini }) =>
		isMini
			? {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					height: `${gridSize * 5}px`,
					width: '100%',
				}
			: {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					height: `${gridSize * 4}px`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					width: `${gridSize * 15}px`,
				},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	{ marginBottom: `${gridSize * 0.75}px` },
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldErrorSkeleton = styled.div<{ isMini?: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isMini }) =>
		isMini
			? {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					height: `${gridSize * 5}px`,
					width: '100%',
				}
			: {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					height: `${gridSize * 4}px`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					width: `${gridSize * 15}px`,
				},
	{
		marginBottom: token('space.075'),

		backgroundColor: token('color.skeleton'),
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelFlex = styled.div({
	display: 'flex',
	alignItems: 'flex-start',
});
